import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@material-ui/core";
import React, { Fragment, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import CloseIcon from "@material-ui/icons/Close";
import { FormattedMessage, useIntl } from "react-intl";
import DebouncedSearchField from "../../../../../../components/DebouncedSearchField";
import useEmployerFindQuery from "../../../../../../../../queries/employer/useEmployerFindQuery";
import cuideoTheme from "../../../../../../../../containers/themes/defaultTheme";
import { IEmployerResponse } from "../../../../../../../../api/employer/employerApiTypes";
import EmployerDetail from "./EmployerDetail";
import {
  apiGetEmployer,
  IGetEmployer,
} from "../../../../../../../../api/employer/apiGetEmployer";

type TOp = "idle" | "fetching_employer";

const StyledDialog = styled(Dialog)<{ op: TOp }>`
  .MuiDialog-paper {
    transition: all 0.25s ease-in-out;
  }

  ${({ op }) => {
    if (op === "idle") {
      return `
      .MuiDialog-paper {
        min-width: 40rem;
        min-height: 29rem;
      }
      `;
    } else {
      return `
      .MuiDialog-paper {
        min-height: 10rem;
        min-width: 20rem;
      }
      `;
    }
  }}
`;

const StyledCloseButton = styled(IconButton)`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
`;

const StyledInputWrapper = styled.div`
  margin-bottom: 1rem;
`;

const StyledTableWrapper = styled(Box)`
  border-top: 2px solid rgba(0, 72, 120, 0.25);
  border-bottom: 2px solid rgba(0, 72, 120, 0.25);
  min-height: 20rem;
`;

const StyledTable = styled(Table)`
  table-layout: fixed;

  .MuiTableCell-root {
    border-bottom: none;
  }

  .MuiTableRow-root {
    cursor: pointer;
    user-select: none;
  }

  .MuiTableCell-body:nth-child(1) {
    width: 14rem;
  }
  .MuiTableCell-body:nth-child(2) {
    width: 5rem;
  }
  .MuiTableCell-body:nth-child(3) {
    width: 5rem;
  }
  .MuiTableCell-body:nth-child(4) {
    width: 6rem;
    text-align: right;
  }
`;

const StyledBodyRow = styled(TableRow)`
  background-color: #fff;
  transition: background-color 0.125s ease-in-out;
  .MuiButton-root {
    transition: opacity 0.125s ease-in-out;
    opacity: 0;
    pointer-events: none;

    &:hover,
    &:focus {
      opacity: 1;
      pointer-events: unset;
    }
  }

  &:hover,
  &:focus {
    .MuiButton-root {
      opacity: 1;
      pointer-events: unset;
    }
    background-color: rgba(235, 194, 0, 0.1);
  }
`;

const StyledOneRow = styled(TableRow)`
  height: 20rem;
`;

const StyledOneCell = styled(TableCell)``;

const StyledOneCellMessage = styled(Typography)`
  font-size: 0.875rem;
  color: #777;
  text-align: center;
  margin: 1rem 2rem;
`;

const StyledTableBodyCell = styled(TableCell)`
  font-size: 0.875rem;
  color: ${cuideoTheme.palette.primary.main};
  line-height: 1.25;
  padding: 0.5rem 0.75rem;
`;

const StyledInTableButton = styled(Button)`
  text-transform: none;
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
`;

const StyledCircularProgressWrapper = styled(Box)`
  margin: 0.25rem auto;
  text-align: center;
`;

const StyledCircularProgress = styled(CircularProgress)``;

interface IEmployerFindModal {
  open: boolean;
  onCancel: () => void;
  onEmployerSelect: (employer: IGetEmployer) => void;
}

const EmployerFindModal = (props: IEmployerFindModal) => {
  const { open, onCancel, onEmployerSelect } = props;
  const { formatMessage } = useIntl();
  const ref = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<{ op: TOp; searchText: string }>({
    op: "idle",
    searchText: "",
  });
  const employersQuery = useEmployerFindQuery(state.searchText);

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        ref.current?.focus();
      }, 100);
    }
  }, [open]);

  const handleSearchChange = (newSearchTerm: string) => {
    if (newSearchTerm !== state.searchText) {
      setState({
        ...state,
        searchText: newSearchTerm,
      });
    }
  };

  const handleEmployerSelect = async (employerId: string) => {
    setState((prevState) => ({ ...prevState, op: "fetching_employer" }));
    let employer = await apiGetEmployer(employerId);
    onEmployerSelect(employer);
    setTimeout(() => {
      setState((prevState) => ({ ...prevState, op: "idle" }));
    }, 500);
  };

  return (
    <StyledDialog open={open} onClose={onCancel} op={state.op}>
      <StyledCloseButton onClick={onCancel}>
        <CloseIcon />
      </StyledCloseButton>
      <DialogTitle>
        <FormattedMessage
          id="EmployerFindModal.Buscar Empleador"
          defaultMessage="Buscar Empleador"
        />
      </DialogTitle>
      <DialogContent>
        {state.op === "idle" && (
          <StyledInputWrapper>
            <DebouncedSearchField
              ref={ref}
              initialValue={state.searchText}
              onDebouncedChange={handleSearchChange}
              placeholder={formatMessage({
                id: "SearchField.Placeholder",
                defaultMessage: "Buscar",
              })}
            />
          </StyledInputWrapper>
        )}
        {state.op === "idle" && (
          <StyledTableWrapper>
            <StyledTable>
              <TableBody>
                {state.searchText === "" ? (
                  <StyledOneRow>
                    <StyledOneCell colSpan={4}>
                      <StyledOneCellMessage>
                        <FormattedMessage
                          id="EmployerFindModal.StartTyping"
                          defaultMessage="Empieza a escribir un nombre, apellido, o DNI para buscar"
                        />
                      </StyledOneCellMessage>
                    </StyledOneCell>
                  </StyledOneRow>
                ) : employersQuery.isLoading ? (
                  <StyledOneRow>
                    <StyledOneCell colSpan={4}>
                      <StyledCircularProgressWrapper>
                        <StyledCircularProgress size={22} />
                      </StyledCircularProgressWrapper>
                      <StyledOneCellMessage>
                        <FormattedMessage
                          id="EmployerFindModal.Buscando Empleadores..."
                          defaultMessage="Buscando Empleadores..."
                        />
                      </StyledOneCellMessage>
                    </StyledOneCell>
                  </StyledOneRow>
                ) : employersQuery.data?.totalItems === 0 ? (
                  <StyledOneRow>
                    <StyledOneCell colSpan={4}>
                      <StyledOneCellMessage>
                        <FormattedMessage
                          id="EmployerFindModal.No se han encontrado resultados"
                          defaultMessage="No se han encontrado resultados"
                        />
                      </StyledOneCellMessage>
                    </StyledOneCell>
                  </StyledOneRow>
                ) : (
                  <Fragment>
                    {employersQuery.data?.employers.map(
                      (employer: IEmployerResponse) => {
                        return (
                          <StyledBodyRow
                            key={employer.id}
                            tabIndex={0}
                            onDoubleClick={() => {
                              handleEmployerSelect(employer.id);
                            }}
                          >
                            <StyledTableBodyCell>
                              <EmployerDetail employer={employer} />
                            </StyledTableBodyCell>
                            <StyledTableBodyCell>
                              <StyledInTableButton
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                  handleEmployerSelect(employer.id);
                                }}
                              >
                                <FormattedMessage
                                  id="EmployerFindModal.Seleccionar"
                                  defaultMessage="Seleccionar"
                                />
                              </StyledInTableButton>
                            </StyledTableBodyCell>
                          </StyledBodyRow>
                        );
                      }
                    )}
                  </Fragment>
                )}
              </TableBody>
            </StyledTable>
          </StyledTableWrapper>
        )}
        {state.op === "fetching_employer" && (
          <StyledCircularProgressWrapper>
            <StyledCircularProgress size={22} />
          </StyledCircularProgressWrapper>
        )}
      </DialogContent>
    </StyledDialog>
  );
};

export default EmployerFindModal;
